import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { TransactionStatus } from '../components/transactionTypes';

interface StatusFilterProps {
    selectedStatus: TransactionStatus | 'all';
    onStatusChange: (status: TransactionStatus | 'all') => void;
}

export const StatusFilter: React.FC<StatusFilterProps> = ({
                                                              selectedStatus,
                                                              onStatusChange,
                                                          }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const statuses: Array<{ value: TransactionStatus | 'all'; label: string }> = [
        { value: 'all', label: 'All ✓' },
        { value: 'pending', label: 'Pending' },
        { value: 'completed', label: 'Completed' },
        { value: 'failed', label: 'Failed' },
        { value: 'canceled', label: 'Canceled' },
        { value: 'refunded', label: 'Refunded' },
    ];

    const selectedLabel = statuses.find(s => s.value === selectedStatus)?.label || 'Status';

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (value: TransactionStatus | 'all') => {
        onStatusChange(value);
        setIsOpen(false);
    };

    return (
        <DropdownContainer ref={dropdownRef}>
            <DropdownButton onClick={() => setIsOpen(!isOpen)}>
                {selectedLabel} <DropdownArrow $isOpen={isOpen}>▼</DropdownArrow>
            </DropdownButton>
            {isOpen && (
                <DropdownMenu>
                    {statuses.map((status) => (
                        <DropdownItem
                            key={status.value}
                            $active={selectedStatus === status.value}
                            onClick={() => handleSelect(status.value)}
                        >
                            {status.label}
                        </DropdownItem>
                    ))}
                </DropdownMenu>
            )}
        </DropdownContainer>
    );
};

const DropdownContainer = styled.div`
    position: relative;
    display: inline-block;
`;

const DropdownButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: #FFFFFF;
    border: 1px solid #D0D0D0;
    border-radius: 8px;
    font-family: SF Pro Display, sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #000000;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 120px;
    justify-content: space-between;

    &:hover {
        border-color: #6777FF;
        background: #F8F9FF;
    }

    @media (max-width: 768px) {
        padding: 6px 12px;
        font-size: 12px;
        min-width: 100px;
    }
`;

const DropdownArrow = styled.span<{ $isOpen: boolean }>`
    font-size: 10px;
    color: #00000080;
    transition: transform 0.2s ease;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownMenu = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    background: #FFFFFF;
    border: 1px solid #D0D0D0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    min-width: 160px;
    overflow: hidden;
    animation: fadeIn 0.2s ease;

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(-8px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @media (max-width: 768px) {
        min-width: 140px;
    }
`;

const DropdownItem = styled.button<{ $active: boolean }>`
    width: 100%;
    padding: 10px 16px;
    background: ${props => props.$active ? '#F8F9FF' : '#FFFFFF'};
    border: none;
    text-align: left;
    font-family: SF Pro Display, sans-serif;
    font-size: 13px;
    font-weight: ${props => props.$active ? '600' : '400'};
    color: ${props => props.$active ? '#6777FF' : '#000000'};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #F8F9FF;
        color: #6777FF;
    }

    &:not(:last-child) {
        border-bottom: 1px solid #F0F0F0;
    }

    @media (max-width: 768px) {
        padding: 8px 12px;
        font-size: 12px;
    }
`;